<template>
  <div id="positions-table">
    <v-data-table
      id="positions-data-table"
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="totalItems"
      sort-by="totalCostWithComissionBaseCurrency"
      :sort-desc="true"
      class="elevation-0 left-sticky-table"
      mobile-breakpoint="0"
      :loading="loading"
      :no-data-text="type ? $t(`views.portfolio.positionsTable.noPosition.${type}`) : ''"
      item-key="_id"
      :item-class="itemRowBackground"
      :footer-props="{
        'items-per-page-options': [5, 10, 15, 20, 30, 50, 100],
      }"
      :items-per-page="itemsPerPage"
      :dense="user.preferences.denseTables"
      @click:row="rowClicked"
      @update:options="updateOptions"
    >
      <template v-if="!brokerAll" slot="body.prepend">
        <tr v-if="totalRow" class="row-total">
          <td class="text-left">
            <span class="row-total-title">TOTAL</span>
          </td>
          <td class="text-right">
            <span />
          </td>
          <td class="text-right">
            <span />
          </td>
          <td class="text-right">
            <span />
          </td>
          <td class="text-right">
            <currency
              v-if="totalRow.totalValueBaseCurrency"
              :value="totalRow.totalValueBaseCurrency"
              :currency="user.baseCurrency"
              strong
            />
          </td>
          <td class="text-right">
            <span />
          </td>
          <td class="text-right">
            <span />
          </td>
          <td class="text-right">
            <span />
          </td>
          <td class="text-right">
            <currency
              v-if="totalRow.totalCostWithComissionBaseCurrency"
              :value="totalRow.totalCostWithComissionBaseCurrency"
              :currency="user.baseCurrency"
              strong
            />
          </td>
          <td class="text-right row-total-currency">
            <TableNetPercent
              v-if="get(totalRow, 'plBaseCurrency', null)"
              :value="get(totalRow, 'plBaseCurrency', null)"
              :currency="user.baseCurrency"
              type="net"
            />
            <span v-else></span>
          </td>
          <td class="text-right row-total-currency">
            <TableNetPercent
              v-if="get(totalRow, 'plBaseCurrencyPercent', null)"
              :value="get(totalRow, 'plBaseCurrencyPercent', null)"
              type="percent"
            />
          </td>
        </tr>
      </template>

      <template v-slot:item.ticker="{ item }">
        <div class="ticker-column items-flex">
          <TableTicker :position="item" notLinkToDetail />
          <div v-if="item.pendingRecreate" class="ml-3">
            <TableCoreRecreating :text="$t('views.common.tableCoreRecreating.text')" />
          </div>
          <template v-else-if="item.incomplete">
            <div class="ml-3 mt-1">
              <WarningIcon :text="$t('views.stocks.stockTable.warning')" />
            </div>
          </template>
        </div>
      </template>
      <template v-slot:item.manager="{ item }">
        <span v-if="item.manager">{{ item.manager }}</span>
        <span v-else></span>
      </template>
      <template v-slot:item.name="{ item }">
        <span v-if="item.name">{{ item.name }}</span>
        <span v-else></span>
      </template>
      <template v-slot:item.type="{ item }">
        <span>{{ $t(`common.positionTypes.${item.type}`) }}</span>
        <span v-if="item.type === 'custom'" class="ml-1">| {{ item.customType }}</span>
      </template>
      <template v-slot:item.country="{ item }">
        {{
          $te(`common.countries.${item.country}`)
            ? $t(`common.countries.${item.country}`) + ' (' + item.country + ')'
            : item.country
            ? item.country
            : '-'
        }}
      </template>
      <template v-slot:item.sector="{ item }">
        <span v-if="item.sector">{{ item.sector }}</span>
        <span v-else></span>
      </template>
      <template v-slot:item.isin="{ item }">
        <span>{{ item.type === 'fund' ? item.ticker : item.isin }}</span>
      </template>
      <template v-slot:item.tags="{ item }">
        <TagGroup v-if="item.tags.length" :tags="item.tags" @tagClicked="tagClicked" />
        <span v-else></span>
      </template>
      <template v-slot:item.positionNumber="{ item }">
        <template v-if="!item.pendingRecreate">
          <currency
            :value="item.positionNumber"
            :currency="item.type === 'cash' ? item.ticker : undefined"
            :decimals="item.type === 'crypto' ? 7 : 2"
          />
        </template>
        <span v-else></span>
      </template>
      <template v-slot:item.marketPrice="{ item }">
        <div class="items-flex">
          <PositionMarketPrice type="position" :position="item" />
          <template v-if="positionNotMarketPrice(item)">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  size="15"
                  class="ml-2"
                  v-on="on"
                  @click.stop="editPosition(item)"
                >
                  mdi-pencil-outline
                </v-icon>
              </template>
              <span>{{ $t('views.stocks.stockTable.editPrice') }}</span>
            </v-tooltip>
          </template>
        </div>
      </template>
      <template v-slot:item.totalValue="{ item }">
        <currency
          v-if="!item.pendingRecreate && item.totalValue"
          :value="item.totalValue"
          :currency="item.currency"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.totalValueBaseCurrency="{ item }">
        <currency
          v-if="!item.pendingRecreate && item.totalValueBaseCurrency"
          :value="item.totalValueBaseCurrency"
          :currency="user.baseCurrency"
          strong
        />
        <span v-else></span>
      </template>
      <template v-slot:item.dailyPlBaseCurrency="{ item }">
        <PositionTablePL :position="item" isDaily type="net" />
      </template>
      <template v-slot:item.dailyPlBaseCurrencyPercent="{ item }">
        <PositionTablePL :position="item" isDaily type="percent" />
      </template>
      <template v-slot:item.hday.mtd.plBaseCurrency="{ item }">
        <TableNetPercent
          v-if="get(item, 'hday.mtd.plBaseCurrency', null)"
          :value="get(item, 'hday.mtd.plBaseCurrency', null)"
          :currency="user.baseCurrency"
          type="net"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.hday.mtd.plBaseCurrencyPercent="{ item }">
        <TableNetPercent
          v-if="get(item, 'hday.mtd.plBaseCurrencyPercent', null)"
          :value="get(item, 'hday.mtd.plBaseCurrencyPercent', null)"
          type="percent"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.hday.ytd.plBaseCurrency="{ item }">
        <TableNetPercent
          v-if="get(item, 'hday.ytd.plBaseCurrency', null)"
          :value="get(item, 'hday.ytd.plBaseCurrency', null)"
          :currency="user.baseCurrency"
          type="net"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.hday.ytd.plBaseCurrencyPercent="{ item }">
        <TableNetPercent
          v-if="get(item, 'hday.ytd.plBaseCurrencyPercent', null)"
          :value="get(item, 'hday.ytd.plBaseCurrencyPercent', null)"
          type="percent"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.hday.years1.plBaseCurrency="{ item }">
        <TableNetPercent
          v-if="get(item, 'hday.years1.plBaseCurrency', null)"
          :value="get(item, 'hday.years1.plBaseCurrency', null)"
          :currency="user.baseCurrency"
          type="net"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.hday.years1.plBaseCurrencyPercent="{ item }">
        <TableNetPercent
          v-if="get(item, 'hday.years1.plBaseCurrencyPercent', null)"
          :value="get(item, 'hday.years1.plBaseCurrencyPercent', null)"
          type="percent"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.hday.years3.plBaseCurrency="{ item }">
        <TableNetPercent
          v-if="get(item, 'hday.years3.plBaseCurrency', null)"
          :value="get(item, 'hday.years3.plBaseCurrency', null)"
          :currency="user.baseCurrency"
          type="net"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.hday.years3.plBaseCurrencyPercent="{ item }">
        <TableNetPercent
          v-if="get(item, 'hday.years3.plBaseCurrencyPercent', null)"
          :value="get(item, 'hday.years3.plBaseCurrencyPercent', null)"
          type="percent"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.hday.years5.plBaseCurrency="{ item }">
        <TableNetPercent
          v-if="get(item, 'hday.years5.plBaseCurrency', null)"
          :value="get(item, 'hday.years5.plBaseCurrency', null)"
          :currency="user.baseCurrency"
          type="net"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.hday.years5.plBaseCurrencyPercent="{ item }">
        <TableNetPercent
          v-if="get(item, 'hday.years5.plBaseCurrencyPercent', null)"
          :value="get(item, 'hday.years5.plBaseCurrencyPercent', null)"
          type="percent"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.averagePriceCost="{ item }">
        <currency
          v-if="!item.pendingRecreate && item.averagePriceCost"
          :value="item.averagePriceCost"
          :currency="item.currency"
          :decimals="3"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.totalCost="{ item }">
        <currency
          v-if="!item.pendingRecreate && item.totalCost"
          :value="item.totalCost"
          :currency="item.currency"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.averagePriceCostWithComissionBaseCurrency="{ item }">
        <currency
          v-if="!item.pendingRecreate && item.averagePriceCostWithComissionBaseCurrency"
          :value="item.averagePriceCostWithComissionBaseCurrency"
          :currency="user.baseCurrency"
          :decimals="3"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.totalCostWithComissionBaseCurrency="{ item }">
        <currency
          v-if="!item.pendingRecreate && item.totalCostWithComissionBaseCurrency"
          :value="item.totalCostWithComissionBaseCurrency"
          :currency="user.baseCurrency"
          strong
        />
        <span v-else></span>
      </template>
      <template v-slot:item.plBaseCurrency="{ item }">
        <PositionTablePL :position="item" type="net" />
      </template>
      <template v-slot:item.plBaseCurrencyPercent="{ item }">
        <PositionTablePL :position="item" type="percent" />
      </template>
      <template v-slot:item.totalDividendWithReturnBaseCurrency="{ item }">
        <currency
          v-if="item.totalDividendWithReturnBaseCurrency"
          :value="item.totalDividendWithReturnBaseCurrency"
          :currency="user.baseCurrency"
        />
        <span v-else></span>
      </template>
      <template v-slot:item.totalDividendYield="{ item }">
        <div v-if="item.totalDividendYield" class="items-flex">
          <currency :value="item.totalDividendYield" neverHide />
          %
        </div>
        <span v-else></span>
      </template>
      <template v-slot:item.isInWatches="{ item }">
        <template v-if="isPositionWatchable(item)">
          <template v-if="item.isInWatches">
            <v-icon small class="ml-3" color="softGreen">
              mdi-bullseye-arrow
            </v-icon>
          </template>
          <template v-else>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  small
                  class="ml-3"
                  color="softTextGrey"
                  v-on="on"
                  @click.stop="createWatchFromPosition(item)"
                >
                  mdi-bullseye
                </v-icon>
              </template>
              <span>{{ $t('views.stocks.stockTable.createWatchFromPosition') }}</span>
            </v-tooltip>
          </template>
        </template>
      </template>
      <template v-slot:item.data-table-expand="{ item }">
        <template v-if="!item.pendingRecreate">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                size="18"
                class="ml-2"
                v-on="on"
                @click.stop="rowClicked(item)"
              >
                mdi-magnify-expand
              </v-icon>
            </template>
            <span>{{ $t(`views.portfolio.positionsTable.showDetail`) }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                size="18"
                class="ml-2"
                v-on="on"
                @click.stop="editPosition(item)"
              >
                mdi-pencil-outline
              </v-icon>
            </template>
            <span>{{ $t('common.edit') }}</span>
          </v-tooltip>
          <template v-if="item.type !== 'cash'">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  size="18"
                  class="ml-2"
                  v-on="on"
                  @click.stop="deletePosition(item)"
                >
                  mdi-delete-outline
                </v-icon>
              </template>
              <span>{{ $t('common.delete') }}</span>
            </v-tooltip>
          </template>
        </template>
      </template>
    </v-data-table>

    <HeadersModal
      v-if="isHeadersModalActive"
      :isModalActive.sync="isHeadersModalActive"
      :tableName="tableName"
      :defaultHeaders="defaultHeaders"
    />
  </div>
</template>

<script>
import gtag from '@/lib/gtag'
import { get, pick } from 'lodash'
import { mapState } from 'vuex'
import { getPositions, createWatch, deletePosition } from '@/lib/api.js'
import tableMixin from '@/views/components/core/tableMixin.js'
import Currency from '@/views/components/common/Currency.vue'
import TableNetPercent from '@/views/components/common/TableNetPercent'
import WarningIcon from '@/views/components/common/WarningIcon'
import TableCoreRecreating from '@/views/components/common/positions/TableCoreRecreating'
import TagGroup from '@/views/components/common/TagGroup'
import PositionMarketPrice from '@/views/components/common/positions/PositionMarketPrice'
import TableTicker from '@/views/components/common/TableTicker'
import PositionTablePL from '@/views/components/common/positions/PositionTablePL'
import { getCurrencySymbol } from '@/lib/utils/currencies.js'
import { getPositionTypes, positionNotMarketPrice } from '@/lib/positions/positions.js'
import { round } from '@/lib/utils/math.js'
const WATCH_POSITION_TYPES = getPositionTypes({ model: 'watch' })

export default {
  components: {
    Currency,
    TableNetPercent,
    TagGroup,
    PositionMarketPrice,
    WarningIcon,
    TableCoreRecreating,
    TableTicker,
    PositionTablePL,
  },
  mixins: [tableMixin],
  props: {
    broker: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      default: null,
    },
    filters: {
      type: Object,
      default: () => {},
    },
    showZeroPositions: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    tableName: 'positions',
    totalRow: {},
  }),
  computed: {
    ...mapState(['user']),
    itemsPerPage() {
      return this.user.preferences.defaultItemsPerPage
    },
    brokerAll() {
      return this.broker.name === 'all'
    },
    defaultHeaders() {
      if (!this.brokerAll) {
        return [
          {
            text: this.$t(`views.portfolio.positionsTable.ticker.all`),
            value: 'ticker',
            divider: true,
            customHideDisabled: true,
            customDragDisabled: true,
          },
          {
            text: this.type
              ? this.$t(`views.portfolio.positionsTable.positionNumber.${this.type}`)
              : '',
            value: 'positionNumber',
            align: 'right',
          },
          {
            text: `${this.$t('views.stocks.stockTable.marketPrice')}`,
            value: 'marketPrice',
            align: 'right',
            sortable: false,
          },
          {
            text: `${this.$t('views.stocks.stockTable.totalValue')}`,
            value: 'totalValue',
            align: 'right',
          },
          {
            text: `${this.$t('views.stocks.stockTable.totalValue')} (${getCurrencySymbol(
              this.user.baseCurrency
            )})`,
            value: 'totalValueBaseCurrency',
            align: 'right',
          },
          {
            text: this.$t('views.stocks.stockTable.averagePriceCost'),
            value: 'averagePriceCost',
            align: 'right',
          },
          {
            text: this.$t('views.stocks.stockTable.totalCost'),
            value: 'totalCost',
            align: 'right',
          },
          {
            text: `${this.$t(
              'views.stocks.stockTable.averagePriceCostWithComission'
            )} (${getCurrencySymbol(this.user.baseCurrency)})`,
            value: 'averagePriceCostWithComissionBaseCurrency',
            align: 'right',
          },
          {
            text: `${this.$t(
              'views.stocks.stockTable.totalCostWithComission'
            )} (${getCurrencySymbol(this.user.baseCurrency)})`,
            value: 'totalCostWithComissionBaseCurrency',
            align: 'right',
          },
          {
            text: `${this.$t('views.stocks.stockTable.pl')} (${getCurrencySymbol(
              this.user.baseCurrency
            )})`,
            value: 'plBaseCurrency',
            align: 'right',
          },
          {
            text: `${this.$t('views.stocks.stockTable.plPercent')}`,
            value: 'plBaseCurrencyPercent',
            align: 'right',
          },
        ]
      }
      return [
        {
          text: this.$t(`views.portfolio.positionsTable.ticker.all`),
          value: 'ticker',
          divider: true,
          customHideDisabled: true,
          customDragDisabled: true,
        },
        { text: this.$t('views.stocks.stockTable.name'), value: 'name' },
        { text: this.$t('views.stocks.stockTable.type'), value: 'type', customHidden: true },
        { text: this.$t('views.stocks.stockTable.country'), value: 'country', customHidden: true },
        {
          text: this.$t('views.portfolio.positionsTable.manager'),
          value: 'manager',
          customHidden: true,
        },
        { text: this.$t('views.stocks.stockTable.sector'), value: 'sector', customHidden: true },
        { text: this.$t('views.stocks.stockTable.isin'), value: 'isin', customHidden: true },
        { text: this.$t('views.stocks.stockTable.tags'), value: 'tags', sortable: false },
        {
          text: this.type
            ? this.$t(`views.portfolio.positionsTable.positionNumber.${this.type}`)
            : '',
          value: 'positionNumber',
          align: 'right',
        },

        {
          text: `${this.$t('views.stocks.stockTable.marketPrice')}`,
          value: 'marketPrice',
          align: 'right',
          sortable: false,
        },
        {
          text: `${this.$t('views.stocks.stockTable.totalValue')}`,
          value: 'totalValue',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.totalValue')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalValueBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.dailyPl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'dailyPlBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.dailyPlPercent')}`,
          value: 'dailyPlBaseCurrencyPercent',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.mtd.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'hday.mtd.plBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.mtd.plPercent')}`,
          value: 'hday.mtd.plBaseCurrencyPercent',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.ytd.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'hday.ytd.plBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.ytd.plPercent')}`,
          value: 'hday.ytd.plBaseCurrencyPercent',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years1.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'hday.years1.plBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years1.plPercent')}`,
          value: 'hday.years1.plBaseCurrencyPercent',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years3.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'hday.years3.plBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years3.plPercent')}`,
          value: 'hday.years3.plBaseCurrencyPercent',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years5.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'hday.years5.plBaseCurrency',
          align: 'right',
          customHidden: true,
        },
        {
          text: `${this.$t('views.stocks.stockTable.hday.years5.plPercent')}`,
          value: 'hday.years5.plBaseCurrencyPercent',
          align: 'right',
          customHidden: true,
        },
        {
          text: this.$t('views.stocks.stockTable.averagePriceCost'),
          value: 'averagePriceCost',
          align: 'right',
        },
        { text: this.$t('views.stocks.stockTable.totalCost'), value: 'totalCost', align: 'right' },
        {
          text: `${this.$t(
            'views.stocks.stockTable.averagePriceCostWithComission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'averagePriceCostWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.totalCostWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalCostWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.pl')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'plBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.plPercent')}`,
          value: 'plBaseCurrencyPercent',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.totalDividendWithReturn')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalDividendWithReturnBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.stocks.stockTable.totalDividendYield')}`,
          value: 'totalDividendYield',
          align: 'right',
          sortable: false, //  no se puede porque no está en bd, se calcula toPublic
        },
        {
          text: `${this.$t('views.stocks.stockTable.isInWatches')}`,
          value: 'isInWatches',
          align: 'center',
          sortable: false, // no se puede porque no está en bd, se calcula toPublic
        },
        { text: this.$t('views.stocks.stockTable.notes'), value: 'notes', sortable: false },
        {
          text: '',
          value: 'data-table-expand',
          sortable: false,
          align: 'right',
          customHideDisabled: true,
          customSecondaryText: `${this.$t('views.stocks.stockTable.editRemove')}`,
        },
      ]
    },
    useAlwaysDefaultHeaders() {
      return !this.brokerAll
    },
  },
  watch: {
    async type() {
      // console.log('PortfolioTable: watch type', this.type)
      if (this.type) await this.loadItems({ setFirstPage: true })
    },
    filters: {
      async handler() {
        // console.log('PortfolioTable: watch filters', this.type)
        await this.loadItems({ setFirstPage: true })
      },
      deep: true,
    },
    async showZeroPositions() {
      // console.log('PortfolioTable: watch showZeroPositions', this.type)
      await this.loadItems({ setFirstPage: true })
    },
  },
  methods: {
    get,
    round,
    positionNotMarketPrice,
    rowClicked(item) {
      if (this.brokerAll) this.$emit('tickerClicked', item)
    },
    tagClicked(tag) {
      if (tag) this.$emit('filterByTag', tag)
    },
    itemRowBackground(item) {
      let className = ''

      if (!this.brokerAll) className = 'row-broker-id'
      if (item.pendingRecreate) className = `${className} row-recreating`
      else if (item.incomplete) className = `${className} row-advise`

      return className
    },
    isPositionWatchable(item) {
      return WATCH_POSITION_TYPES.indexOf(item.type) > -1
    },
    async loadItems({ setFirstPage = false } = {}) {
      // console.log('loadItems')
      this.loading = true

      if (setFirstPage) this.setFirstPage()

      try {
        const filter = {
          type:
            this.type === 'all'
              ? undefined
              : this.type === 'allNeCash'
              ? { $ne: 'cash' }
              : this.type,
          positionNumber: this.showZeroPositions ? undefined : { $ne: 0 },
          ...this.filters,
        }
        const { page, limit, sort } = this.getTablePaginationOptions(this.options)
        const response = await getPositions({
          brokerId: !this.brokerAll ? this.broker._id : undefined,
          filter,
          page,
          limit,
          sort,
        })
        if (!this.brokerAll) {
          this.totalRow = get(response, 'data.summaryByBrokerId')
        }
        this.setTablePaginatedResponse(response)

        this.filtersFirstLoaded = true // controlamos que ya cargamos por primera vez para no traer dos veces posiciones
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.loading = false
    },
    editPosition(position) {
      this.$emit('editPosition', position)
    },
    async deletePosition(position) {
      if (
        await this.$root.$confirm({
          title: this.$t('views.stocks.stockTable.confirm.title'),
          message: this.$t('views.stocks.stockTable.confirm.message'),
        })
      ) {
        this.loading = true
        try {
          await deletePosition(position)
          await this.$emit('loadAllPortfolioByBroker')
          this.$root.$snackbar.success({ text: this.$t('common.deleteOk') })
          gtag.pageview({
            pagePath: '/portfolio/delete/success',
            pageTitle: 'portfolio|delete|success',
            pageContentGroup: 'modal',
          })
        } catch (error) {
          this.$root.$snackbar.error({ text: this.$t('common.deleteError') })
          gtag.pageview({
            pagePath: '/portfolio/delete/error',
            pageTitle: 'portfolio|delete|error',
            pageContentGroup: 'modal',
          })
        }
        this.loading = false
      }
    },
    async createWatchFromPosition(position) {
      try {
        const watchAttributes = [
          'type',
          'ticker',
          'country',
          'exchange',
          'currency',
          'name',
          'sector',
          'manager',
          'tags',
        ]
        const watch = pick(position, watchAttributes)
        await createWatch(watch)
        this.loadItems()
        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })
      } catch (error) {
        this.error = get(error, 'data.name', null)
        if (!this.error) {
          this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        } else {
          this.$root.$snackbar.error({ text: this.$t(`apiErrors.${this.error}`) })
        }
      }
    },
  },
}
</script>

<style lang="scss">
#positions-table {
  #positions-data-table {
    .v-data-table-header th {
      white-space: nowrap;
    }
    .items-flex {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      &.ticker-column {
        justify-content: flex-start;
        align-items: flex-start;
      }
    }

    table > tbody > tr:not(.row-total, .row-advise, .row-recreating, .row-broker-id):hover {
      cursor: pointer;
    }

    .row-total-title {
      font-weight: 500;
    }
    .row-total-currency {
      #currency {
        font-size: 16px;
      }
    }
  }
}
</style>
